import {
    Component,
    ContentChildren,
    ElementRef,
    HostBinding,
    Input,
    QueryList,
    AfterContentInit, // Import new hooks
    AfterViewInit,
    OnDestroy, booleanAttribute
} from '@angular/core';
import {CardActionDirective} from './directives/card-action.directive';
import {Element} from '@angular/compiler';
import {Subscription} from 'rxjs'; // Import Subscription

@Component({
    selector: 'ui-card',
    templateUrl: './ui-card.component.html',
    styleUrls: ['./ui-card.component.scss', 'ui-card.contextual-styles.scss'],
    standalone: false
})
// Implement the hooks
export class UiCardComponent implements AfterContentInit, AfterViewInit, OnDestroy {
    hasActions = false;
    hasMultipleActions = false;

    private _calculatedVariant = 'default';
    private _calculatedDisplay = 'padded';
    private _calculatedBehaviour = 'single';
    private _actionsSub: Subscription;

    @Input() cardTitle: string = undefined;
    @Input() cardSubtitle: string = undefined;
    @Input() expandable = false;
    @Input() expanded = true;
    @Input() variant: 'default' | 'internal' | 'success' | 'warning' | 'borderless' | 'auto' = 'auto';
    @Input() shape: 'round' | 'square' = 'round';
    @Input() loading = false;
    @Input() disabled = false;
    @Input() actionsBehaviour: 'auto' | 'single' | 'multiple' = 'auto';
    @Input() display: 'full-space' | 'padded' | 'auto' = 'auto';
    @Input() behaviour: 'auto' | 'single' | 'sibling' | 'multiple' = 'auto';
    @Input({transform: booleanAttribute}) hasTabs = false;

    @ContentChildren(CardActionDirective) actions: QueryList<CardActionDirective>;

    constructor(public el: ElementRef) {}

    @HostBinding('class')
    get hostClasses(): string {
        const classes = [
            this.disabled ? 'disabled' : this._calculatedVariant,
            this.shape,
            this.hasHeader && 'has-header', // hasHeader getter is now also pure
            this.cardTitle && 'has-title',
            `${this._calculatedDisplay}-card`,
            `${this._calculatedBehaviour}-card`,
            this.hasTabs && 'has-tabs',
            this.hasActions && 'has-actions',
            this.hasMultipleActions && 'has-multiple-actions'
        ];

        return classes.filter(Boolean).join(' ');
    }

    get hasHeader() {
        return this.hasActions || !!this.cardTitle;
    }

    ngAfterContentInit(): void {
        setTimeout(() => {
            this.updateActionProperties();
        });

        this._actionsSub = this.actions.changes.subscribe(() => {
            this.updateActionProperties();
        });
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this._calculatedVariant = this.autoVariant();
            this._calculatedDisplay = this.autoDisplay();
            this._calculatedBehaviour = this.autoBehaviour();
        });
    }

    ngOnDestroy(): void {
        this._actionsSub?.unsubscribe();
    }

    private updateActionProperties(): void {
        this.hasActions = this.actions.length > 0;

        switch (this.actionsBehaviour) {
            case 'single':
                this.hasMultipleActions = false;
                break;
            case 'multiple':
                this.hasMultipleActions = true;
                break;
            case 'auto':
            default:
                this.hasMultipleActions = this.actions.length > 1;
                break;
        }
    }

    toggleExpand() {
        throw new Error('Funcionalidade ainda não implementada');
        // if (this.expandable) {
        //     this.expanded = !this.expanded;
        // }
    }

    private getChildren(parent: Element | HTMLElement) {
        return Array.from((parent as HTMLElement)?.children ?? []);
    }

    private autoDisplay() {
        if (this.display !== 'auto') {
            return this.display;
        }

        const children =
            this.getChildren(
                this.getChildren(
                    this.getChildren(this.el.nativeElement)
                        .find(c => c.classList.contains('ui-card')) as HTMLElement
                ).find(c => c.classList.contains('ui-card__content-wrapper')) as HTMLElement
            );

        if (['ngui-map'].includes(children[0]?.localName?.toLowerCase()) && children.length === 1) {
            return 'full-space';
        } else {
            return 'padded';
        }
    }

    private autoBehaviour() {
        if (this.behaviour !== 'auto') {
            return this.behaviour;
        }

        const parent = (this.el.nativeElement as HTMLElement).parentElement;
        const siblings = Array.from(parent?.children ?? []);

        if (siblings.filter((s) => s.localName === 'ui-card').length === 1) {
            return 'single';
        } else if (siblings.length === 2) {
            return 'sibling';
        } else {
            return 'multiple';
        }
    }

    private autoVariant() {
        if (this.variant !== 'auto') {
            return this.variant;
        }

        const uiCards: HTMLElement[] = [];
        let parent = (this.el.nativeElement as HTMLElement).parentElement;

        while (parent) {
            if (parent.localName === 'ui-card') {
                uiCards.push(parent);
            }

            if (uiCards.length >= 2) {
                return 'internal'
            }

            parent = parent.parentElement;
        }

        return 'default';
    }
}
